import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiCheck, FiX, FiLock, FiGrid, FiSave } from 'react-icons/fi';
import { permissionAPI, roleAPI } from '../../utils/api';
import '../styles/PermissionManagement.css';

const normalizeActivePermissionIds = (permissions) => (Array.isArray(permissions) ? permissions : [])
  .filter((p) => p?.isActive)
  .map((p) => p?.id)
  .map(Number)
  .filter((id) => Number.isInteger(id) && id > 0);

const buildPermissionIndex = (permissions) => {
  const index = new Map();
  (Array.isArray(permissions) ? permissions : []).forEach((permission) => {
    const moduleKey = String(permission?.module || '');
    const actionKey = String(permission?.action || '');
    if (!moduleKey || !actionKey) return;
    index.set(`${moduleKey}::${actionKey}`, permission);
  });
  return index;
};

const PermissionMatrixCell = ({ permission }) => (
  <td className="text-center">
    {permission ? (
      <Badge bg={permission.isActive ? 'success' : 'danger'}>
        {permission.isActive ? <FiCheck /> : <FiX />}
      </Badge>
    ) : (
      <Badge bg="light" text="dark">
        <FiX />
      </Badge>
    )}
  </td>
);

const buildRolePermissionMap = async ({ roles = [], activePermissionIds }) => {
  const map = {};
  await Promise.all((roles || []).map(async (role) => {
    try {
      if (String(role?.roleType || '').toUpperCase() === 'ADMIN') {
        map[role.id] = activePermissionIds;
        return;
      }
      const response = await roleAPI.getRolePermissions(role.id);
      const permissionIds = Array.isArray(response?.data?.permissionIds) ? response.data.permissionIds : [];
      map[role.id] = permissionIds.map(Number).filter((id) => Number.isInteger(id));
    } catch {
      map[role.id] = [];
    }
  }));
  return map;
};

const loadPermissionManagementData = async () => {
  const permsResponse = await permissionAPI.getAllPermissions();
  const permissions = permsResponse?.success && permsResponse?.data ? permsResponse.data : [];
  const activePermissionIds = normalizeActivePermissionIds(permissions);

  const rolesResponse = await roleAPI.getAllRoles();
  const roles = rolesResponse?.success && rolesResponse?.data ? rolesResponse.data : [];
  const rolePermissionMap = await buildRolePermissionMap({ roles, activePermissionIds });

  return { permissions, roles, activePermissionIds, rolePermissionMap };
};

const PermissionManagement = () => {
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showRolePermModal, setShowRolePermModal] = useState(false);
  const [activeTab, setActiveTab] = useState('permissions');

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    module: '',
    action: '',
    isActive: true
  });

  const [selectedPermission, setSelectedPermission] = useState(null);
  const [permissionToDelete, setPermissionToDelete] = useState(null);
  const [selectedRoleForPerms, setSelectedRoleForPerms] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [rolePermissionMap, setRolePermissionMap] = useState({});
  const [adminRoleAllPermissions, setAdminRoleAllPermissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Permission modules and actions
  const modules = ['users', 'projects', 'files', 'departments', 'roles', 'permissions', 'audit', 'settings'];
  const actions = ['create', 'read', 'update', 'delete', 'download', 'export', 'import'];

  /**
   * Fetch permissions and roles
   */
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      const loaded = await loadPermissionManagementData();
      setPermissions(loaded.permissions);
      setRoles(loaded.roles);
      setAdminRoleAllPermissions(loaded.activePermissionIds);
      setRolePermissionMap(loaded.rolePermissionMap);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const checks = [
      [String(formData.name || '').trim().length > 0, 'Permission name is required'],
      [String(formData.code || '').trim().length > 0, 'Permission code is required'],
      [Boolean(formData.module), 'Module is required'],
      [Boolean(formData.action), 'Action is required']
    ];

    const failed = checks.find(([ok]) => !ok);
    if (failed) {
      setError(failed[1]);
      return false;
    }
    return true;
  };

  /**
   * Handle create permission
   */
  const handleCreatePermission = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const response = await permissionAPI.createPermission(formData);
      if (response.success && response.data) {
        setSuccess('Permission created successfully!');
        setPermissions([...permissions, response.data]);
        setShowCreateModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Create error:', err);
      setError(err.message || 'Failed to create permission');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle update permission
   */
  const handleUpdatePermission = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const response = await permissionAPI.updatePermission(selectedPermission.id, formData);
      if (response.success && response.data) {
        setSuccess('Permission updated successfully!');
        setPermissions(permissions.map(p => p.id === selectedPermission.id ? response.data : p));
        setShowEditModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(err.message || 'Failed to update permission');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle delete permission
   */
  const handleDeletePermission = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      await permissionAPI.deletePermission(permissionToDelete.id);
      setSuccess('Permission deleted successfully!');
      setPermissions(permissions.filter(p => p.id !== permissionToDelete.id));
      setShowDeleteModal(false);
      setPermissionToDelete(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete permission');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle edit modal open
   */
  const handleOpenEditModal = (perm) => {
    setSelectedPermission(perm);
    setFormData({
      name: perm.name,
      code: perm.code,
      description: perm.description || '',
      module: perm.module,
      action: perm.action,
      isActive: perm.isActive
    });
    setShowEditModal(true);
  };

  /**
   * Handle view modal open
   */
  const handleOpenViewModal = (perm) => {
    setSelectedPermission(perm);
    setShowViewModal(true);
  };

  /**
   * Handle delete modal open
   */
  const handleOpenDeleteModal = (perm) => {
    setPermissionToDelete(perm);
    setShowDeleteModal(true);
  };

  /**
   * Handle role permissions
   */
  const handleOpenRolePermModal = (role) => {
    setSelectedRoleForPerms(role);
    const isAdminRole = String(role?.roleType || '').toUpperCase() === 'ADMIN';
    const assigned = isAdminRole
      ? adminRoleAllPermissions
      : (Array.isArray(rolePermissionMap?.[role.id]) ? rolePermissionMap[role.id] : []);
    setRolePermissions(assigned.map(Number).filter((id) => Number.isInteger(id)));
    setShowRolePermModal(true);
  };

  /**
   * Toggle permission for role
   */
  const handleToggleRolePermission = (permId) => {
    if (String(selectedRoleForPerms?.roleType || '').toUpperCase() === 'ADMIN') return;
    if (rolePermissions.includes(permId)) {
      setRolePermissions(rolePermissions.filter(id => id !== permId));
    } else {
      setRolePermissions([...rolePermissions, permId]);
    }
  };

  /**
   * Save role permissions
   */
  const handleSaveRolePermissions = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      if (String(selectedRoleForPerms?.roleType || '').toUpperCase() === 'ADMIN') {
        // ADMIN permissions are implicit; nothing to persist.
        setRolePermissionMap((prev) => ({
          ...prev,
          [selectedRoleForPerms.id]: [...adminRoleAllPermissions]
        }));
        setSuccess('ADMIN has all permissions by default.');
        setShowRolePermModal(false);
        return;
      }

      const response = await roleAPI.updateRolePermissions(selectedRoleForPerms.id, rolePermissions);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to save role permissions');
      }

      setRolePermissionMap((prev) => ({
        ...prev,
        [selectedRoleForPerms.id]: [...rolePermissions]
      }));
      setSuccess('Role permissions updated successfully!');
      setShowRolePermModal(false);
    } catch (err) {
      setError(err.message || 'Failed to save role permissions');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      module: '',
      action: '',
      isActive: true
    });
  };

  /**
   * Get permissions for module
   */
  // removed unused helper to silence lint

  /**
   * Get permissions count for role
   */
  const getRolePermissionsCount = (roleId) => {
    const assigned = rolePermissionMap?.[roleId];
    return Array.isArray(assigned) ? assigned.length : 0;
  };

  /**
   * Filter permissions
   */
  const filteredPermissions = permissions.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.module.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const permissionIndex = buildPermissionIndex(permissions);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading permissions...</p>
      </div>
    );
  }

  return (
    <Container fluid className="perm-management">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="management-header mb-4">
        <div>
          <h2>Manage Permissions</h2>
          <p className="text-muted">Configure system permissions and access control</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => setShowCreateModal(true)}
          className="create-btn"
        >
          <FiPlus className="me-2" />
          Add Permission
        </Button>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-navigation mb-4">
        <Card className="shadow-sm">
          <Card.Body className="p-0">
            <ul className="nav nav-tabs perm-tabs" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'permissions' ? 'active' : ''}`}
                  id="permissions-tab"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'permissions'}
                  onClick={() => setActiveTab('permissions')}
                >
                  <FiCheck className="me-2" />
                  All Permissions
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'roles' ? 'active' : ''}`}
                  id="roles-tab"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'roles'}
                  onClick={() => setActiveTab('roles')}
                >
                  <FiLock className="me-2" />
                  Role Permissions
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === 'matrix' ? 'active' : ''}`}
                  id="matrix-tab"
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'matrix'}
                  onClick={() => setActiveTab('matrix')}
                >
                  <FiGrid className="me-2" />
                  Permission Matrix
                </button>
              </li>
            </ul>
          </Card.Body>
        </Card>
      </div>

      {/* Tabs Content */}
      <div className="tab-content">
        {/* Permissions Tab */}
        <div className={`tab-pane ${activeTab === 'permissions' ? 'show active' : ''}`} id="permissions" role="tabpanel">
          {/* Search */}
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <Form.Control
                type="text"
                placeholder="Search permissions by name, code, or module..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
              />
            </Card.Body>
          </Card>

          {/* Permissions Table */}
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              {filteredPermissions.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>No permissions found</p>
                  <Button
                    variant="primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <FiPlus className="me-2" />
                    Create First Permission
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="perm-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Module</th>
                        <th>Action</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPermissions.map(perm => (
                        <tr key={perm.id} className="perm-row">
                          <td>
                            <strong>{perm.name}</strong>
                          </td>
                          <td>
                            <code className="perm-code">{perm.code}</code>
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="module-badge">
                              {perm.module}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="info" className="action-badge">
                              {perm.action}
                            </Badge>
                          </td>
                          <td>
                            <small>{perm.description || '-'}</small>
                          </td>
                          <td>
                            <Badge bg={perm.isActive ? 'success' : 'secondary'}>
                              {perm.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleOpenViewModal(perm)}
                              className="me-2"
                              title="View"
                            >
                              <FiEye size={14} />
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenEditModal(perm)}
                              className="me-2"
                              title="Edit"
                            >
                              <FiEdit2 size={14} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleOpenDeleteModal(perm)}
                              title="Delete"
                            >
                              <FiTrash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Role Permissions Tab */}
        <div className={`tab-pane ${activeTab === 'roles' ? 'show active' : ''}`} id="roles" role="tabpanel">
          <Row>
            {roles.length === 0 ? (
              <Col>
                <Card className="shadow-sm">
                  <Card.Body className="text-center py-5 text-muted">
                    <p>No roles found</p>
                  </Card.Body>
                </Card>
              </Col>
            ) : (
              roles.map(role => (
                <Col md={6} lg={4} key={role.id} className="mb-3">
                  <Card className="role-card shadow-sm h-100">
                    <Card.Body>
                      <h5 className="mb-3">{role.roleName || role.name || 'Unnamed Role'}</h5>
                      <p className="text-muted small mb-3">{role.description}</p>

                      <div className="role-stats mb-3">
                        <div className="stat">
                          <span className="stat-label">Permissions</span>
                          <span className="stat-value">{getRolePermissionsCount(role.id)}</span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Status</span>
                          <Badge bg={role.isActive ? 'success' : 'secondary'} className="ms-2">
                            {role.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>

                      <Button
                        variant="primary"
                        size="sm"
                        className="w-100"
                        onClick={() => handleOpenRolePermModal(role)}
                      >
                        <FiCheck className="me-2" />
                        Manage Permissions
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </div>

        {/* Permission Matrix Tab */}
        <div className={`tab-pane ${activeTab === 'matrix' ? 'show active' : ''}`} id="matrix" role="tabpanel">
          <Card className="shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">Permission Matrix - Modules vs Actions</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table className="matrix-table mb-0">
                  <thead>
                    <tr>
                      <th>Module / Action</th>
                      {actions.map(action => (
                        <th key={action} className="text-center action-header">
                          {action.charAt(0).toUpperCase() + action.slice(1)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map(module => (
                      <tr key={module}>
                        <td className="module-name">
                          <strong>{module.charAt(0).toUpperCase() + module.slice(1)}</strong>
                        </td>
                        {actions.map((action) => (
                          <PermissionMatrixCell
                            key={`${module}-${action}`}
                            permission={permissionIndex.get(`${module}::${action}`)}
                          />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiPlus className="me-2" />
            Create New Permission
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleCreatePermission}>
            {/* Name */}
            <Form.Group className="mb-3">
              <Form.Label>Permission Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Create User"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Code */}
            <Form.Group className="mb-3">
              <Form.Label>Permission Code <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., user.create"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={submitting}
                className="form-input"
              />
              <small className="text-muted">Use format: module.action</small>
            </Form.Group>

            <Row>
              {/* Module */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Module <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.module}
                    onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                    disabled={submitting}
                    className="form-input"
                  >
                    <option value="">Select Module</option>
                    {modules.map(m => (
                      <option key={m} value={m}>
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Action */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Action <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.action}
                    onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                    disabled={submitting}
                    className="form-input"
                  >
                    <option value="">Select Action</option>
                    {actions.map(a => (
                      <option key={a} value={a}>
                        {a.charAt(0).toUpperCase() + a.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter permission description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Active */}
            <Form.Check
              type="checkbox"
              label="Permission is Active"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              disabled={submitting}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreatePermission}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <FiPlus className="me-2" />
                Create Permission
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" dialogClassName="permission-fixed-modal">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEdit2 className="me-2" />
            Edit Permission
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleUpdatePermission}>
            {/* Name */}
            <Form.Group className="mb-3">
              <Form.Label>Permission Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Create User"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Code */}
            <Form.Group className="mb-3">
              <Form.Label>Permission Code <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., user.create"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={submitting}
                className="form-input"
              />
              <small className="text-muted">Use format: module.action</small>
            </Form.Group>

            <Row>
              {/* Module */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Module <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.module}
                    onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                    disabled={submitting}
                    className="form-input"
                  >
                    <option value="">Select Module</option>
                    {modules.map(m => (
                      <option key={m} value={m}>
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Action */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Action <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.action}
                    onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                    disabled={submitting}
                    className="form-input"
                  >
                    <option value="">Select Action</option>
                    {actions.map(a => (
                      <option key={a} value={a}>
                        {a.charAt(0).toUpperCase() + a.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter permission description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            {/* Active */}
            <Form.Check
              type="checkbox"
              label="Permission is Active"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              disabled={submitting}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdatePermission}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <FiSave className="me-2" />
                Update Permission
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg" dialogClassName="permission-fixed-modal">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEye className="me-2" />
            Permission Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedPermission && (
            <div className="perm-details">
              <div className="detail-group">
                <label>Permission Name</label>
                <p>{selectedPermission.name}</p>
              </div>

              <div className="detail-group">
                <label>Permission Code</label>
                <p><code>{selectedPermission.code}</code></p>
              </div>

              <div className="detail-group">
                <label>Module</label>
                <p>
                  <Badge bg="light" text="dark">
                    {selectedPermission.module}
                  </Badge>
                </p>
              </div>

              <div className="detail-group">
                <label>Action</label>
                <p>
                  <Badge bg="info">
                    {selectedPermission.action}
                  </Badge>
                </p>
              </div>

              <div className="detail-group">
                <label>Description</label>
                <p>{selectedPermission.description || 'No description'}</p>
              </div>

              <div className="detail-group">
                <label>Status</label>
                <p>
                  <Badge bg={selectedPermission.isActive ? 'success' : 'danger'}>
                    {selectedPermission.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </p>
              </div>

              <div className="detail-group">
                <label>Created Date</label>
                <p>{new Date(selectedPermission.createdAt).toLocaleString()}</p>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowViewModal(false);
              handleOpenEditModal(selectedPermission);
            }}
          >
            <FiEdit2 className="me-2" />
            Edit
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {permissionToDelete && (
            <div className="delete-confirmation">
              <div className="warning-icon">⚠️</div>
              <p className="mt-3">
                Are you sure you want to delete <strong>{permissionToDelete.name}</strong>?
              </p>
              <p className="text-muted small">
                This action cannot be undone. The permission will be permanently removed.
              </p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setPermissionToDelete(null);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeletePermission}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <FiTrash2 className="me-2" />
                Delete Permission
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Role Permissions Modal */}
      <Modal show={showRolePermModal} onHide={() => setShowRolePermModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiCheck className="me-2" />
            Manage Permissions for {selectedRoleForPerms?.roleName || selectedRoleForPerms?.name || 'Role'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="role-permissions-grid">
            {permissions.length === 0 ? (
              <p className="text-muted">No permissions available</p>
            ) : (
              permissions.map(perm => (
                <div key={perm.id} className="perm-checkbox">
                  <Form.Check
                    type="checkbox"
                    id={`perm-${perm.id}`}
                    label={`${perm.name} (${perm.module}.${perm.action})`}
                    checked={rolePermissions.includes(perm.id)}
                    onChange={() => handleToggleRolePermission(perm.id)}
                    disabled={submitting || String(selectedRoleForPerms?.roleType || '').toUpperCase() === 'ADMIN'}
                  />
                </div>
              ))
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => setShowRolePermModal(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveRolePermissions}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FiSave className="me-2" />
                Save Permissions
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PermissionManagement;
